import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

export interface AuthUser {
  id: number;
  username: string;
  companyName: string | null;
  isAdmin: boolean;
  email: string | null;
  emailVerified?: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string, rememberMe?: boolean) => Promise<string | null>;
  logout: () => Promise<void>;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    fetch("/api/auth/me", { credentials: "include" })
      .then(r => r.ok ? r.json() as Promise<AuthUser> : null)
      .then(u => { setUser(u ?? null); setLoading(false); })
      .catch(() => { setUser(null); setLoading(false); });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (
    username: string, password: string, rememberMe = false,
  ): Promise<string | null> => {
    const r = await fetch("/api/auth/login", {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password, rememberMe }),
    });
    const data = await r.json().catch(() => ({ error: "Network error" })) as {
      error?: string; token?: string; username?: string; ok?: boolean;
    };
    if (!r.ok) return data.error ?? "Login failed";

    // Persist the auth token so TubeScan (which shares the same API server)
    // can pick it up from localStorage when the user navigates there.
    // TubeScan's App.tsx skips the session-cookie fallback and goes straight
    // to the login screen when no "ts:auth_token" is found — so we must store
    // it here or any cross-app navigation forces a redundant re-login.
    if (data.token) {
      try { localStorage.setItem("ts:auth_token", data.token); } catch { /* storage blocked */ }
      try { sessionStorage.setItem("ts:auth_token", data.token); } catch { /* blocked */ }
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `td_tok=${data.token}; Path=/; Max-Age=2592000; SameSite=None${secure}`;
    }

    // Apply auth state directly from the login response (same payload as /auth/me)
    // so we don't need a second round-trip that could fail if cookies are blocked.
    if (data.username) {
      setUser({ id: 0, username: data.username, companyName: null, isAdmin: false, email: null, emailVerified: false });
      setLoading(false);
      // Fetch the full user record in the background to populate isAdmin etc.
      fetch("/api/auth/me", { credentials: "include" })
        .then(r2 => r2.ok ? r2.json() as Promise<AuthUser> : null)
        .then(u => { if (u) setUser(u); setLoading(false); })
        .catch(() => { setLoading(false); });
    } else {
      await new Promise<void>(resolve => {
        fetch("/api/auth/me", { credentials: "include" })
          .then(r2 => r2.ok ? r2.json() as Promise<AuthUser> : null)
          .then(u => { setUser(u ?? null); setLoading(false); resolve(); })
          .catch(() => { setUser(null); setLoading(false); resolve(); });
      });
    }
    return null;
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be called inside <AuthProvider>");
  return ctx;
}
